/**
 * Slot Check Search (Universal Search) session state.
 *
 * - Saved search: the last search the user ran (query, grouping, drawer, scroll,
 *   map view). While it exists the header globe icon is gold and tapping it
 *   restores exactly that view. sessionStorage, so it is per tab / login
 *   session; CLEAR_STORE (logout) clears it.
 * - Entry intent: the one-shot hand-off when an EGM or game is tapped. It
 *   survives the trip through the normal casino entry (ChooseCasinos →
 *   Random Outcomes / Link Player → Explore) and is consumed by Explore, which
 *   applies the game filter after the entry defaults.
 */

export type UniversalQueryType = 'text' | 'game' | 'manufacturer';
export type UniversalGroupBy = 'casino' | 'manufacturer';

export type UniversalMapView = { lng: number; lat: number; zoom: number };

export type UniversalSavedSearch = {
  query: string;
  qType: UniversalQueryType;
  group: UniversalGroupBy;
  /** Drawer height, percent of the viewport (open part). */
  drawerPercent?: number;
  scrollTop?: number;
  map?: UniversalMapView | null;
  /** Group keys expanded with "Show all". */
  expanded?: string[];
  savedAt: number;
};

export type UniversalSearchEntryIntent = {
  token: string;
  casinoId: number;
  /** Chip label in Explore (game name). */
  filterName: string;
  /** Detail ids of the matching EGMs in that casino (mnum / slot_number). */
  detailIds: string[];
  /** Set when an EGM card was tapped: open its EGM Details too. */
  openDetailId?: string | null;
  /** 'select' until ChooseCasinos has started the entry, then 'filter'. */
  stage: 'select' | 'filter';
  createdAt: number;
};

export const UNIVERSAL_SEARCH_SAVED_KEY = 'slotcheck:universalSearch:saved';
export const UNIVERSAL_SEARCH_ENTRY_KEY = 'slotcheck:universalSearch:entry';
/** An entry not completed within this window (e.g. user backed out of Link Player) is dropped. */
export const UNIVERSAL_SEARCH_ENTRY_TTL_MS = 10 * 60 * 1000;

type Listener = () => void;
const listeners = new Set<Listener>();

function storageOrNull(storage?: Storage | null): Storage | null {
  if (storage !== undefined) return storage;
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function readJson<T>(key: string, storage?: Storage | null): T | null {
  const s = storageOrNull(storage);
  if (!s) return null;
  try {
    const raw = s.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown, storage?: Storage | null): void {
  const s = storageOrNull(storage);
  if (!s) return;
  try {
    if (value === null) s.removeItem(key);
    else s.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / disabled: the feature still works, it just won't restore */
  }
}

function notify(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* ignore listener errors */
    }
  });
}

/** Re-render hook point (CasinoNavBar globe color). Returns unsubscribe. */
export function subscribeUniversalSearch(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isValidSaved(v: unknown): v is UniversalSavedSearch {
  if (!v || typeof v !== 'object') return false;
  const o = v as UniversalSavedSearch;
  return typeof o.query === 'string' && o.query.trim().length > 0;
}

export function readSavedUniversalSearch(storage?: Storage | null): UniversalSavedSearch | null {
  const v = readJson<UniversalSavedSearch>(UNIVERSAL_SEARCH_SAVED_KEY, storage);
  if (!isValidSaved(v)) return null;
  return {
    query: v.query,
    qType: v.qType === 'game' || v.qType === 'manufacturer' ? v.qType : 'text',
    group: v.group === 'manufacturer' ? 'manufacturer' : 'casino',
    drawerPercent: typeof v.drawerPercent === 'number' ? v.drawerPercent : undefined,
    scrollTop: typeof v.scrollTop === 'number' && v.scrollTop >= 0 ? v.scrollTop : 0,
    map:
      v.map && Number.isFinite(v.map.lng) && Number.isFinite(v.map.lat) && Number.isFinite(v.map.zoom)
        ? v.map
        : null,
    expanded: Array.isArray(v.expanded) ? v.expanded.filter((x) => typeof x === 'string') : [],
    savedAt: typeof v.savedAt === 'number' ? v.savedAt : 0,
  };
}

/** Merge-update the saved search (notifies only when it starts / stops existing). */
export function writeSavedUniversalSearch(
  patch: Partial<UniversalSavedSearch> & { query?: string },
  storage?: Storage | null
): void {
  const prev = readSavedUniversalSearch(storage);
  const next = { ...(prev || {}), ...patch, savedAt: Date.now() } as UniversalSavedSearch;
  if (!isValidSaved(next)) return;
  writeJson(UNIVERSAL_SEARCH_SAVED_KEY, next, storage);
  if (!prev || prev.query !== next.query) notify();
}

export function clearSavedUniversalSearch(storage?: Storage | null): void {
  const had = !!readSavedUniversalSearch(storage);
  writeJson(UNIVERSAL_SEARCH_SAVED_KEY, null, storage);
  if (had) notify();
}

/** Gold globe icon: a saved search exists for this session. */
export function hasActiveUniversalSearch(storage?: Storage | null): boolean {
  return !!readSavedUniversalSearch(storage);
}

// ---------------------------------------------------------------------------
// Entry intent
// ---------------------------------------------------------------------------

export function setUniversalSearchEntry(
  intent: Omit<UniversalSearchEntryIntent, 'token' | 'stage' | 'createdAt'>,
  storage?: Storage | null,
  now = Date.now()
): UniversalSearchEntryIntent {
  const full: UniversalSearchEntryIntent = {
    ...intent,
    detailIds: (intent.detailIds || []).map(String).filter(Boolean),
    token: `${now}-${Math.random().toString(36).slice(2, 8)}`,
    stage: 'select',
    createdAt: now,
  };
  writeJson(UNIVERSAL_SEARCH_ENTRY_KEY, full, storage);
  return full;
}

export function peekUniversalSearchEntry(
  storage?: Storage | null,
  now = Date.now()
): UniversalSearchEntryIntent | null {
  const v = readJson<UniversalSearchEntryIntent>(UNIVERSAL_SEARCH_ENTRY_KEY, storage);
  if (!v || typeof v !== 'object' || !Number.isFinite(Number(v.casinoId)) || !v.token) return null;
  if (!(now - Number(v.createdAt) < UNIVERSAL_SEARCH_ENTRY_TTL_MS)) {
    writeJson(UNIVERSAL_SEARCH_ENTRY_KEY, null, storage);
    return null;
  }
  return { ...v, casinoId: Number(v.casinoId), detailIds: Array.isArray(v.detailIds) ? v.detailIds.map(String) : [] };
}

/** ChooseCasinos started the casino entry: Explore may now apply the filter. */
export function markUniversalSearchEntryStarted(token: string, storage?: Storage | null): void {
  const v = peekUniversalSearchEntry(storage);
  if (!v || v.token !== token) return;
  writeJson(UNIVERSAL_SEARCH_ENTRY_KEY, { ...v, stage: 'filter' }, storage);
}

/**
 * Explore: take (and clear) the pending filter for the casino now in view.
 * Returns null when there is none, it is for another casino, or the entry has
 * not been started by ChooseCasinos yet.
 */
export function takeUniversalSearchCasinoFilter(
  casinoId: unknown,
  storage?: Storage | null,
  now = Date.now()
): UniversalSearchEntryIntent | null {
  const v = peekUniversalSearchEntry(storage, now);
  if (!v || v.stage !== 'filter') return null;
  if (Number(casinoId) !== v.casinoId) return null;
  writeJson(UNIVERSAL_SEARCH_ENTRY_KEY, null, storage);
  return v;
}

export function clearUniversalSearchEntry(storage?: Storage | null): void {
  writeJson(UNIVERSAL_SEARCH_ENTRY_KEY, null, storage);
}

/** Logout (CLEAR_STORE): the saved search and any pending entry are per session. */
export function clearUniversalSearchSession(storage?: Storage | null): void {
  clearUniversalSearchEntry(storage);
  clearSavedUniversalSearch(storage);
}
